// 認証系 POST のクライアント側キャッシュ層 (Documents/Plan/AuthCache)。
// - in-flight 重複排除 (single-flight): 進行中の同一リクエストは 1 本の Promise を共有する
// - 短期 TTL 結果キャッシュ (既定 10 秒): 成功 (2xx) 応答を一定時間だけ再利用する
// 対象は CACHEABLE_ENDPOINTS の許可リストのみ。それ以外は素通しする。
// クライアント側でのみ有効 (import.meta.client)。サーバ実行ではモジュールシングルトンが
// 全ユーザー横断で共有され漏洩するため、素の fetch にフォールバックする。
// 強制再取得 (updateUserInfo({ t })) は内部ヘッダ X-Cache-Bust-T の値をキャッシュキーに含めて無効化する。
// URL/クエリには手を加えない (openapi-fetch のパス完全一致を壊さないため)。

/** クライアント側キャッシュ無効化用の内部ヘッダ名。値はキー算出に使い、実 fetch 前に削除する */
export const CACHE_BUST_HEADER = "X-Cache-Bust-T";

type CacheableEndpoint = {
  /** URL.pathname の完全一致で判定する */
  path: string
  /** キーに含めるリクエストヘッダ名 (アイデンティティ) */
  keyHeaders?: string[]
  /** 個別 TTL (ms)。未指定は DEFAULT_TTL_MS */
  ttlMs?: number
};

/** キャッシュ対象 API。今後対象を増やす場合はここへ追記する */
const CACHEABLE_ENDPOINTS = [
  { path: "/api/v2/Cookie2Token" },
  { path: "/api/v2/EntraId2Token", keyHeaders: ["X-EntraID-IdToken"] },
  { path: "/api/v2/UserInfo", keyHeaders: ["Authorization"] },
] as const satisfies readonly CacheableEndpoint[];

const DEFAULT_TTL_MS = 10_000;
/** resultCache の件数上限。キーに Authorization を含むためトークン更新で孤児が蓄積し得る */
const RESULT_CACHE_MAX_ENTRIES = 32;
/** TTL 切れエントリをアクセス無しでも回収する周期 (ms) */
const RESULT_CACHE_PURGE_INTERVAL_MS = 30_000;

// 再構築 (new Response) で扱えない / 扱うと情報が失われるレスポンス。
// - opaqueredirect (redirect:"manual" で捕捉した 3xx。status=0, type が失われる)
// - status=0 (ネットワークエラー等の擬似レスポンス。new Response が RangeError で throw する)
// これらはキャッシュ・再構築せず、生の Response をそのまま呼び出し側へ返す。
const isNonReconstructable = (response: Response) =>
  response.type === "opaqueredirect" || response.status === 0;

type CacheEntry = {
  settledAt: number
  /** 格納時の TTL。purge はここを参照する (エンドポイント個別 TTL 対応) */
  ttlMs: number
  bytes: ArrayBuffer
  status: number
  statusText: string
  headers: [string, string][]
};

// in-flight で共有する解決値。
// - CacheEntry: 再構築可能なレスポンス。各 waiter が toResponse で個別に Response 化する。
// - Response: 非再構築レスポンス (opaqueredirect / status=0)。各 waiter が clone して受け取る
//   (opaqueredirect は body 未消費のため clone 可能。全 waiter が type/status を失わずに得られる)。
type InflightResult = CacheEntry | Response;

const isResponse = (value: InflightResult): value is Response => value instanceof Response;

// 共有解決値を waiter ごとの Response に変換する。Response は clone、CacheEntry は再構築。
const resolveShared = (value: InflightResult): Response =>
  isResponse(value) ? value.clone() : toResponse(value);

// クライアント (ブラウザのタブ) 単位 = 単一ユーザーで安全なモジュールシングルトン
const resultCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<InflightResult>>();

/**
 * TTL 切れを削除し、残件数が上限超なら挿入順 (LRU 近似) の先頭から間引く。
 * Map は挿入順を保持するため、get 時に delete+set で後段へ回すとヒット側が優先される。
 */
const purgeResultCache = (now = Date.now()): void => {
  for (const [key, entry] of resultCache) {
    if (now - entry.settledAt >= entry.ttlMs) {
      resultCache.delete(key);
    }
  }
  while (resultCache.size > RESULT_CACHE_MAX_ENTRIES) {
    const oldest = resultCache.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    resultCache.delete(oldest);
  }
};

// クライアントのみ定期 purge (TTL 切れ孤児を同一キー再アクセス無しでも回収)
if (import.meta.client && typeof setInterval === "function") {
  setInterval(() => purgeResultCache(), RESULT_CACHE_PURGE_INTERVAL_MS);
}

const findEndpoint = (pathname: string): CacheableEndpoint | undefined =>
  CACHEABLE_ENDPOINTS.find(endpoint => endpoint.path === pathname);

/** ボディを持てない status はボディ無しで Response を生成する */
const isBodylessStatus = (status: number) => status === 101 || status === 204 || status === 205 || status === 304;

const toResponse = (entry: CacheEntry): Response => {
  // status=0 は new Response が RangeError で throw するため有効なエラーステータス (500) に丸める防御。
  // 非再構築レスポンス (opaqueredirect / status=0) は Response として共有し
  // toResponse を通さない (resolveShared が clone で返す) ため、通常ここには status=0 は来ない。
  const status = entry.status === 0 ? 500 : entry.status;
  return new Response(isBodylessStatus(status) ? null : entry.bytes, {
    status,
    statusText: entry.statusText,
    headers: entry.headers,
  });
};

/** X-Cache-Bust-T を取り除いた Request を返す (元 Request は変更しない) */
const stripCacheBustHeader = (request: Request): Request => {
  if (!request.headers.has(CACHE_BUST_HEADER)) {
    return request;
  }
  const headers = new Headers(request.headers);
  headers.delete(CACHE_BUST_HEADER);
  return new Request(request, { headers });
};

/** キャッシュキーを生成する。method / pathname / body / cache-bust 値 / 識別ヘッダ。URL クエリは使わない */
const buildCacheKey = async (endpoint: CacheableEndpoint, request: Request): Promise<string> => {
  const url = new URL(request.url);
  const bodyText = await request.clone().text();
  const cacheBustValue = request.headers.get(CACHE_BUST_HEADER) ?? "";
  const keyHeaderValues = (endpoint.keyHeaders ?? []).map(name => request.headers.get(name) ?? "");
  return [request.method.toUpperCase(), url.pathname, bodyText, cacheBustValue, ...keyHeaderValues].join("\n");
};

/**
 * 対象エンドポイントのリクエストを single-flight + 短期 TTL でキャッシュして返す。
 * cachingFetch から「キャッシュする」と判定された場合のみ呼ばれる。
 */
const fetchWithCache = async (endpoint: CacheableEndpoint, request: Request): Promise<Response> => {
  const key = await buildCacheKey(endpoint, request);
  // X-Cache-Bust-T はキー算出にのみ使い、サーバへは送らない
  const outgoing = stripCacheBustHeader(request);
  const ttlMs = endpoint.ttlMs ?? DEFAULT_TTL_MS;

  // 結果キャッシュ (未失効のみ)。ヒット時は delete+set で真の LRU 化
  const now = Date.now();
  purgeResultCache(now);
  const cached = resultCache.get(key);
  if (cached && now - cached.settledAt < cached.ttlMs) {
    resultCache.delete(key);
    resultCache.set(key, cached);
    return toResponse(cached);
  }
  if (cached) {
    resultCache.delete(key);
  }

  // in-flight 共有 (成否に関わらず同一 Promise を共有して重複排除する)
  // CacheEntry は各呼び出しが toResponse で個別に Response 化し、
  // 非再構築レスポンス (opaqueredirect / status=0) は Response を共有し各 waiter が clone する。
  // これにより並行呼び出しでも全 waiter が opaqueredirect の type/status を失わずに受け取れる。
  const existing = inflight.get(key);
  if (existing) {
    return existing.then(resolveShared);
  }

  const promise = (async (): Promise<InflightResult> => {
    const response = await globalThis.fetch(outgoing);
    // 非再構築レスポンスはキャッシュせず Response を共有する (waiter は clone で受け取る)。
    if (isNonReconstructable(response)) {
      return response;
    }
    const bytes = await response.arrayBuffer();
    const entry: CacheEntry = {
      settledAt: Date.now(),
      ttlMs,
      bytes,
      status: response.status,
      statusText: response.statusText,
      headers: [...response.headers.entries()],
    };
    // 成功応答のみ結果キャッシュ (一過性の失敗を TTL 間ピン留めしない)
    if (response.ok) {
      resultCache.set(key, entry);
      purgeResultCache(entry.settledAt);
    }
    return entry;
  })();
  inflight.set(key, promise);
  try {
    // originator も waiter と同じ変換を通す。
    // CacheEntry は再構築、Response (opaqueredirect / status=0) は clone して返す
    // (元 Response 本体は誰も直接消費せず、全員が clone を受け取るため二重消費しない)。
    return resolveShared(await promise);
  }
  finally {
    inflight.delete(key);
  }
};

/** リクエストがキャッシュ対象なら対応する設定を返す。対象外なら undefined */
const resolveCacheableEndpoint = (request: Request): CacheableEndpoint | undefined => {
  if (request.method.toUpperCase() !== "POST") {
    return undefined; // 対象は POST のみ
  }
  return findEndpoint(new URL(request.url).pathname);
};

export const cachingFetch: typeof fetch = async (input, init) => {
  // サーバ実行 (将来 ssr:true 時) はモジュールシングルトン共有 = 漏洩のため素の fetch にフォールバックする
  if (!import.meta.client) {
    return globalThis.fetch(input as RequestInfo, init); // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
  }
  const request = input instanceof Request ? input : new Request(input as RequestInfo, init); // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
  const endpoint = resolveCacheableEndpoint(request);
  // キャッシュする / しない の分岐はこの 1 箇所のみ
  return endpoint
    ? await fetchWithCache(endpoint, request)
    : await globalThis.fetch(input as RequestInfo, init); // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
};

/**
 * キャッシュを無効化する。predicate 省略時は全消去。
 * ログアウト等、同一タブでアカウントが変わり得る場面で呼ぶ。
 * @param predicate 引数は対象エントリの URL.pathname。true を返したエントリを削除する
 */
export const clearRequestCache = (predicate?: (path: string) => boolean): void => {
  if (!predicate) {
    resultCache.clear();
    return;
  }
  for (const key of [...resultCache.keys()]) {
    // キーの 2 行目が pathname (cachingFetch のキー生成と対応)
    const pathname = key.split("\n")[1] ?? "";
    if (predicate(pathname)) {
      resultCache.delete(key);
    }
  }
};
